/* eslint-disable no-console */
import { Stack, Typography } from '@mui/material'
import FormSubjectAnswerHandler from '../components/FormSubjectAnswerHandler'
import FormVictimAnswerHandler from '../components/FormVictimAnswerHandler'
import colors from '../constants/colors'
import { store } from '../store'
import { IAnswer, IBackendAnswer, ISubanswers, IWorkplace } from '../store/reports/types'
import timeUtils from './timeUtils'

export interface ISubanswerType {
  question: string
  answer: string
}

export interface IAnswerWithSubanswer extends IAnswer {
  subAnswer?: ISubanswers[]
}

const resolveQuestionStyle = (isRoot: boolean) => {
  const questionVariant = isRoot ? 'h3' : 'labelBold'
  const color = isRoot ? colors.TAG_BLUE : colors.BLACK
  const marginBottom = isRoot ? '1.8rem' : '.5rem'

  return {
    marginBottom: marginBottom,
    typography: questionVariant,
    color: color,
    ...(isRoot ? { display: 'none' } : {}),
  }
}

const resolveAnswerStyle = (isRoot: boolean) => {
  const answerVariant = isRoot ? 'textLarge' : 'labelRegular'

  return { marginBottom: '1.8rem', color: colors.BLACK, typography: answerVariant }
}

const checkIfThereAreSubquestions = (answer: IBackendAnswer) => {
  let answersWithSubanswers = [...answer.answers] as IAnswerWithSubanswer[]

  if (answer.subanswers.length) {
    answer.subanswers.forEach(sub => {
      answersWithSubanswers = answersWithSubanswers.map(x => {
        if (x.question_option?.id === sub?.option) {
          return {
            ...x,
            subAnswer: [...(x.subAnswer ?? []), sub],
          }
        }
        return x
      })
    })
  }

  return answersWithSubanswers
}

const valueRender = (value: string | number | undefined) => (value ? value : '-')

// Sub-answer question types that hold the reporter's details (identified / anonymous)
const REPORTER_FORM_TYPES = ['FORM_REPORTER', 'FORM']

// City, state and country are looked up from the zip; Work Site only exists on older reports
const renderWorkplace = (workplace?: IWorkplace) => {
  const fields = [
    ...(workplace?.work_site ? [{ label: 'Work Site', value: workplace.work_site }] : []),
    { label: 'Worksite Zip Code', value: workplace?.work_zip_code },
    { label: 'Work City', value: workplace?.work_city },
    { label: 'Work State', value: workplace?.work_state },
    { label: 'Work Country', value: workplace?.work_country },
    { label: 'Specific Worksite', value: workplace?.specific_worksite },
  ]

  return fields.map(field => (
    <Stack key={field.label}>
      <Typography variant='labelBold'>{field.label}</Typography>
      <Typography variant='bodyRegular'>{valueRender(field.value)}</Typography>
    </Stack>
  ))
}

const singleSelectHandler = (answer: IBackendAnswer) => {
  const answersWithSubanswers = checkIfThereAreSubquestions(answer)
  if (!answersWithSubanswers.length) {
    return (
      <Stack key={answer?.uuid} className={'t-single-select-section'}>
        <Typography variant='textLarge' sx={{ color: colors.BLACK }}>
          -
        </Typography>
      </Stack>
    )
  }

  const mainQuestion = answersWithSubanswers[0]?.question_option.text
  const subAnswers = answersWithSubanswers[0]?.subAnswer

  // "Identify yourself" card: show the worksite from the report itself, because the reporter
  // sub-answer is often not matched (sub-answer option is not saved) and would hide it
  const isReporterForm = (sub?: ISubanswers) =>
    REPORTER_FORM_TYPES.includes(sub?.question?.variant?.type ?? '')
  const showWorkplace =
    answer?.subanswers?.some(isReporterForm) && !subAnswers?.some(isReporterForm)

  return (
    <Stack key={answer?.uuid} className={'t-single-select-section'}>
      <span className='t-report-answer'>{mainQuestion}</span>

      {subAnswers?.map(sub => {
        return answersParser(sub as unknown as IBackendAnswer)
      })}

      {showWorkplace && (
        <Stack sx={{ marginTop: '1.8rem' }} spacing={2} className={'t-workplace-section'}>
          {renderWorkplace(store.getState()?.reports?.openedCase?.workplace)}
        </Stack>
      )}
    </Stack>
  )
}

const multipleSelectHandler = (answer: IBackendAnswer) => {
  const answersWithSubanswers = checkIfThereAreSubquestions(answer)
  if (!answersWithSubanswers.length) {
    return (
      <Stack key={answer?.uuid} className='t-multiple-select-section'>
        <Typography variant='textLarge' sx={{ color: colors.BLACK }}>
          -
        </Typography>
      </Stack>
    )
  }

  return answersWithSubanswers.map((item, index) => {
    const mainQuestion = item.question_option.text
    const subAnswers = item?.subAnswer

    return (
      <Stack key={`${answer.text}${index}`} className='t-multiple-select-section'>
        <Typography variant='textLarge' sx={{ color: colors.BLACK }}>
          {mainQuestion}
        </Typography>

        {subAnswers?.map(sub => {
          return answersParser(sub as unknown as IBackendAnswer)
        })}
      </Stack>
    )
  })
}

const checkBoxSelectHandler = (answer: IBackendAnswer) => {
  const isRootQuestion = answer.question.is_root
  const question = answer.question?.variant?.label
  const answerContent = answer.answers.length
    ? answer.answers.map(a => `${a.question_option?.text} `)
    : '-'
  return (
    <Stack key={answer?.uuid}>
      {answerContent !== '-' && (
        <>
          <Typography sx={resolveQuestionStyle(isRootQuestion)}>{question}</Typography>
          <Typography sx={resolveAnswerStyle(isRootQuestion)}>{answerContent}</Typography>
        </>
      )}
      {answerContent !== '-' &&
        answer.subanswers.map(item => {
          return answersParser(item as unknown as IBackendAnswer)
        })}
    </Stack>
  )
}

const dropdownSelectHandler = (answer: IBackendAnswer) => {
  const isRootQuestion = answer.question.is_root
  const question = answer.question?.variant?.label
  const answerContent = answer.answers[0]?.question_option?.text || '-'
  return (
    <Stack key={answer?.uuid}>
      <Typography sx={resolveQuestionStyle(isRootQuestion)}>{question}</Typography>
      <Typography sx={resolveAnswerStyle(isRootQuestion)}>{answerContent}</Typography>
      {answerContent !== '-' &&
        answer.subanswers.map(item => {
          return answersParser(item as unknown as IBackendAnswer)
        })}
    </Stack>
  )
}

const logicalHandler = (answer: IBackendAnswer) => {
  const isRootQuestion = answer.question.is_root
  const question = answer.question.text
  const answerContent = answer.answers[0]?.question_option?.text || '-'

  return (
    <Stack key={answer?.uuid} className='t-logical-section'>
      <Typography sx={resolveQuestionStyle(isRootQuestion)}>{question}</Typography>
      <Typography
        className={isRootQuestion ? 't-report-answer' : ''}
        sx={resolveAnswerStyle(isRootQuestion)}
      >
        {answerContent}
      </Typography>
      {answerContent !== '-' &&
        answer.subanswers.map(item => {
          return answersParser(item as unknown as IBackendAnswer)
        })}
    </Stack>
  )
}

const numberHandler = (answer: IBackendAnswer) => {
  const isRootQuestion = answer.question.is_root
  const question = answer.question.variant.label || '-'
  const number = answer.text || '-'

  return (
    <Stack key={answer?.uuid} className='t-number-section'>
      <Typography sx={resolveQuestionStyle(isRootQuestion)}>{question}</Typography>
      <Typography variant='textLarge' sx={{ color: colors.BLACK }}>
        {number}
      </Typography>
    </Stack>
  )
}

const dateHandler = (answer: IBackendAnswer) => {
  const isRootQuestion = answer.question.is_root
  const question = answer.question.text
  const date = answer.text || '2023-01-20T02:00:00Z'

  return (
    <Stack key={answer?.uuid} className='t-date-section'>
      <Typography sx={resolveQuestionStyle(isRootQuestion)}>{question}</Typography>
      <Typography sx={resolveAnswerStyle(isRootQuestion)}>
        {timeUtils.getFormatedDate(date)}
      </Typography>
    </Stack>
  )
}

const openHandler = (answer: any) => {
  const isRootQuestion = answer.question.is_root
  let question = answer.question?.label || '-'
  const answerContent = answer.text || '-'
  const weaponAnswersQuestion = 'ab1b2ef2-83b3-11ee-a8f7-ebe9cc45f1ea'
  const reporterBeforeQuestion = 'ab1b996e-83b3-11ee-a8f7-ebe9cc45f1ea'
  if (answer.question.uuid === reporterBeforeQuestion) {
    question = answer.question.text || '-'
  }
  const renderWeponAnswers = Boolean(question && answerContent !== '-')
  const renderOption = (
    <Stack key={answer?.uuid} className={'t-open-section'}>
      <Typography sx={resolveQuestionStyle(isRootQuestion)}>{question}</Typography>
      <Typography sx={resolveAnswerStyle(isRootQuestion)}>{answerContent}</Typography>
    </Stack>
  )
  if (answer.question.uuid === weaponAnswersQuestion) {
    return (
      <Stack key={answer?.uuid} className={'t-open-section'}>
        <Typography sx={resolveQuestionStyle(isRootQuestion)}>
          {answer?.question?.variant?.label}
        </Typography>
        <Typography sx={resolveAnswerStyle(isRootQuestion)}>{answerContent}</Typography>
      </Stack>
    )
  }

  if (!answer.is_root && renderWeponAnswers) {
    return renderOption
  }

  if (answer.is_root) {
    return renderOption
  }
}

const formReporterHandler = (answer: IBackendAnswer) => {
  const state = store.getState()
  const report = state?.reports?.openedCase

  const renderReportersData = state?.reports?.openedCase?.reporters?.map(
    (reporter: { full_name?: string; phone_number?: string; email?: string; id: number }) => {
      return (
        <Stack sx={{ marginTop: '1.8rem' }} spacing={2} key={reporter.id}>
          <Stack>
            <Typography variant='labelBold'>My name</Typography>
            <Typography variant='bodyRegular'>{valueRender(reporter?.full_name)}</Typography>
          </Stack>
          <Stack>
            <Typography variant='labelBold'>Phone Number</Typography>
            <Typography variant='bodyRegular'>{valueRender(reporter?.phone_number)}</Typography>
          </Stack>
          <Stack>
            <Typography variant='labelBold'>Email Address</Typography>
            <Typography variant='bodyRegular'>{valueRender(reporter?.email)}</Typography>
          </Stack>
          {renderWorkplace(report?.workplace)}
        </Stack>
      )
    },
  )

  return (
    <Stack className={'t-form-reporter-section'} key={answer?.uuid}>
      {renderReportersData}
    </Stack>
  )
}

const formReporterAnonymousHandler = (answer: IBackendAnswer) => {
  const state = store.getState()
  const report = state?.reports?.openedCase

  return (
    <Stack
      sx={{ marginTop: '2rem' }}
      spacing={2}
      key={answer?.uuid}
      className={'t-form-reporter-anonymous-section'}
    >
      {renderWorkplace(report?.workplace)}
    </Stack>
  )
}

const formVictimsHandler = (answer: IBackendAnswer) => {
  const state = store.getState()
  const victims = state?.reports.openedCase?.victims
  if (victims?.length > 0) {
    const nodes = victims?.map((victim: any, index: number) => {
      return <FormVictimAnswerHandler victim={victim} victimIndex={index} question={answer.question} key={victim?.id} />
    })
    return nodes
  }

  return (
    <>
      <Typography variant='bodyRegular'>-</Typography>
    </>
  )
}

const formSubjectHandler = (answer: IBackendAnswer) => {
  const state = store.getState()
  const subjects = state?.reports.openedCase?.subjects

  if (subjects?.length > 0) {
    const nodes = subjects?.map((subject: any, index: number) => {
      return <FormSubjectAnswerHandler subject={subject} subjectIndex={index} key={subject?.id} />
    })
    return nodes
  }

  return (
    <>
      <Typography variant='bodyRegular'>-</Typography>
    </>
  )
}

const questionTypes: { [key: string]: any } = {
  SINGLE_SELECT_HORIZONTAL: singleSelectHandler,
  SINGLE_SELECT_VERTICAL: singleSelectHandler,
  MULTIPLE_SELECT: multipleSelectHandler,
  LOGICAL_HORIZONTAL: logicalHandler,
  LOGICAL_VERTICAL: logicalHandler,
  NUMBER: numberHandler,
  DATE: dateHandler,
  OPEN: openHandler,
  OPEN_UNKNOW: openHandler,
  CHECKBOX: checkBoxSelectHandler,
  DROPDOWN: dropdownSelectHandler,
  FORM_REPORTER: formReporterHandler,
  FORM: formReporterAnonymousHandler,
  FORM_VICTIM: formVictimsHandler,
  FORM_SUBJECT: formSubjectHandler,
}

const answersParser = (answer: IBackendAnswer, victimQuestion?: boolean) => {
  if (answer?.victim && !victimQuestion) {
    return <span className='t-font-size-body t-font-bold'>{'-'}</span>
  }
  const questionVariantType = answer.question.variant.type
  if (questionVariantType in questionTypes) return questionTypes[questionVariantType](answer)

  return <></>
}

export default answersParser
